import { useEffect, useState } from "react";
import axios from "axios";
import { AlertCircle, CheckCircle2, Loader2, Phone, ShieldCheck } from "lucide-react";
import { BrandButton } from "@/components/ui/brand-button";
import { QUOTE_API_PATH } from "@/lib/quote-options";
import { captureAttribution, getGclid } from "@/lib/attribution";
import { usePhoneNumber } from "@/hooks/usePhoneNumber";
import { cn } from "@/lib/utils";
import { TRACKS } from "@/lib/tracks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}${QUOTE_API_PATH}`;

const PHONE_RE = /^\d{10}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Three options rather than the full five from the wizard: enough to separate a
// buyer from a browser without turning a 30-second form back into a survey.
// Values must stay inside the backend's VALID_TIMELINES set.
const QUICK_TIMELINES = [
    { id: "asap", label: "ASAP" },
    { id: "1-3-months", label: "1–3 months" },
    { id: "exploring", label: "Just looking" },
];

// B2B tracks only. Values must stay inside the backend's VALID_ROLES set.
const B2B_ROLES = [
    { id: "property-manager", label: "Property manager" },
    { id: "owner", label: "Owner" },
    { id: "facility-manager", label: "Facility manager" },
    { id: "developer", label: "Developer" },
    { id: "other", label: "Other" },
];

// Per-track wording. Commercial/industrial buyers book a site walk, not an
// "estimate", and expect a COI — say so up front.
const COPY = {
    residential: {
        eyebrow: "Free Estimate",
        title: (name) => `Get your ${name ? name.toLowerCase() + " " : ""}quote.`,
        sub: "Takes about 30 seconds. We reply within one business day.",
        submit: "Get My Free Quote",
        success: "We'll call you within one business day to book your free on-site estimate. Need it sooner?",
        cityPlaceholder: "Toronto",
    },
    b2b: {
        eyebrow: "Site Walk & Quote",
        title: (name) => (name ? `${name} quote.` : "Request a site walk."),
        sub: "30 seconds. COI & WSIB clearance on request. One business-day response.",
        submit: "Request My Site Walk",
        success: "We'll call you within one business day to schedule a site walk and scope the work. Need it sooner?",
        cityPlaceholder: "Mississauga",
    },
};

const INITIAL = {
    full_name: "",
    phone: "",
    email: "",
    city: "",
    timeline: "",
    service: "",
    role: "",
};

const selectBase =
    "block w-full bg-white border-2 border-brand-dark/15 px-4 py-3 text-base text-brand-dark focus:border-brand-cyan focus:outline-none focus:ring-0 transition-colors duration-150";

const inputBase =
    "block w-full bg-white border-2 border-brand-dark/15 px-4 py-3 text-base text-brand-dark placeholder:text-brand-gray focus:border-brand-cyan focus:outline-none focus:ring-0 transition-colors duration-150";

const Field = ({ label, error, children }) => (
    <label className="block">
        <span className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/70 mb-1.5">
            {label}
        </span>
        {children}
        {error ? (
            <span className="mt-1 flex items-center gap-1.5 text-xs text-brand-coral">
                <AlertCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                {error}
            </span>
        ) : null}
    </label>
);

/**
 * Short quote form that sits on the service page itself.
 *
 * The long path (six CTAs, all links to /contact, then a four-step wizard whose
 * first step asks which service the visitor already clicked) produced one form
 * submission in the site's history. This posts to the same
 * /api/quote-submission endpoint with the same contract — the fields the wizard
 * collects across four steps are either implied by the page or defaulted, so
 * nothing downstream in Mongo, the CRM webhook or GA4 has to change.
 *
 * Props:
 * - `track`: "residential" | "commercial" | "industrial" — sets track and
 *   property_type on the payload, the wording, and (B2B) adds a role field.
 * - `serviceSlug` + `serviceName`: fixed service (service detail pages).
 *   Omit `serviceSlug` to show a service dropdown built from the track's
 *   service list (track homes, blog posts); `defaultService` preselects one.
 * - `defaultCity`: pre-fills the city (city landing pages).
 *   Every slug must be in the backend's VALID_SERVICES; "other" = not sure.
 * - `source`: short label for where the form sits, sent with the GA4 event.
 */
const QuickQuoteForm = ({
    track = "residential",
    serviceSlug,
    serviceName,
    defaultService = "",
    defaultCity = "",
    title,
    source = "",
    className,
}) => {
    const trackCfg = TRACKS[track] || TRACKS.residential;
    const isB2B = trackCfg.key !== "residential";
    const copy = isB2B ? COPY.b2b : COPY.residential;
    const serviceOptions = serviceSlug
        ? []
        : trackCfg.services.map((s) => ({ slug: s.path.split("/").pop(), label: s.label }));
    const [data, setData] = useState(() => ({
        ...INITIAL,
        city: defaultCity,
        service:
            !serviceSlug && serviceOptions.some((o) => o.slug === defaultService)
                ? defaultService
                : "",
    }));
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState("");
    const { phone: displayPhone, phoneHref } = usePhoneNumber();

    // The ad's click id lands on this page, not on /contact — capture it here so
    // it survives to the submission even if the visitor wanders first.
    useEffect(() => {
        captureAttribution();
    }, []);

    const update = (patch) => {
        setData((prev) => ({ ...prev, ...patch }));
        setErrors((prev) => {
            const next = { ...prev };
            Object.keys(patch).forEach((k) => delete next[k]);
            return next;
        });
    };

    const validate = () => {
        const e = {};
        if (!data.full_name.trim()) e.full_name = "Your name is required.";
        if (!PHONE_RE.test(data.phone.replace(/\D/g, ""))) {
            e.phone = "Enter a 10-digit phone number.";
        }
        if (!EMAIL_RE.test(data.email.trim())) e.email = "Enter a valid email address.";
        if (!data.city.trim()) e.city = "Which city is the project in?";
        if (!data.timeline) e.timeline = "Pick a rough timeline.";
        if (!serviceSlug && !data.service) e.service = "Which service do you need?";
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }
        setSubmitting(true);
        setServerError("");

        const params =
            typeof window !== "undefined"
                ? new URLSearchParams(window.location.search)
                : new URLSearchParams();
        const sourcePage =
            typeof window !== "undefined" ? window.location.pathname : trackCfg.home;
        const service = serviceSlug || data.service;

        try {
            const payload = {
                // Collected here.
                full_name: data.full_name.trim(),
                phone: data.phone.replace(/\D/g, ""),
                email: data.email.trim(),
                city: data.city.trim(),
                timeline: data.timeline,
                // Implied by the page the visitor is standing on.
                services: [service],
                track: trackCfg.key,
                property_type: trackCfg.key,
                role: isB2B ? data.role : "",
                // Not asked for — the sales call scopes these. "unsure" and
                // "anytime" are the honest neutral values in the backend's
                // enums; nothing here invents a preference the visitor gave.
                project_size: "unsure",
                best_time: "anytime",
                description: "",
                postal_code: "",
                referral_source: "",
                // Attribution. source_page is the real landing page here, where
                // the wizard could only ever report /contact.
                source_page: sourcePage,
                utm_source: (params.get("utm_source") || "").trim().slice(0, 80),
                utm_content: (params.get("utm_content") || "").trim().slice(0, 200),
                gclid: getGclid(),
            };
            const res = await axios.post(API_URL, payload, {
                headers: { "Content-Type": "application/json" },
            });
            if (res.status >= 200 && res.status < 300) {
                setSubmitted(true);
                if (typeof window !== "undefined" && typeof window.gtag === "function") {
                    window.gtag("event", "generate_lead", {
                        source_page: payload.source_page,
                        track: trackCfg.key,
                        service,
                        form_location: source || "quick_form",
                    });
                }
            } else {
                setServerError("Something went wrong. Please call us or try again.");
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (Array.isArray(detail) && detail.length > 0) {
                setServerError(detail.map((d) => d.msg || "Invalid field").join(" • "));
            } else if (typeof detail === "string") {
                setServerError(detail);
            } else {
                setServerError(`Could not send your request. Please call ${displayPhone}.`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div
                className={cn("bg-white border-2 border-brand-cyan p-6 sm:p-7", className)}
                data-testid="quickquote-success"
            >
                <CheckCircle2 className="h-10 w-10 text-brand-cyan" strokeWidth={2} />
                <h3 className="mt-4 font-display text-3xl tracking-wide text-brand-dark leading-tight">
                    Request received.
                </h3>
                <p className="mt-3 text-sm text-brand-dark/75 leading-relaxed">
                    {copy.success}
                </p>
                <a
                    href={phoneHref}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand-cyan hover:text-brand-coral transition-colors"
                    data-testid="quickquote-success-call"
                >
                    <Phone className="h-4 w-4" strokeWidth={2.5} />
                    Call {displayPhone}
                </a>
            </div>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            noValidate
            className={cn("bg-white border border-brand-dark/10 p-6 sm:p-7 shadow-xl", className)}
            data-testid="quickquote-form"
        >
            <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-2">
                {copy.eyebrow}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-wide text-brand-dark leading-none">
                {title || copy.title(serviceName)}
            </h2>
            <p className="mt-2.5 text-sm text-brand-dark/70 leading-relaxed">{copy.sub}</p>

            <div className="mt-5 space-y-4">
                {!serviceSlug && (
                    <Field label="Service" error={errors.service}>
                        <select
                            name="service"
                            className={selectBase}
                            value={data.service}
                            onChange={(e) => update({ service: e.target.value })}
                            data-testid="quickquote-service"
                        >
                            <option value="">Choose a service…</option>
                            {serviceOptions.map((o) => (
                                <option key={o.slug} value={o.slug}>
                                    {o.label}
                                </option>
                            ))}
                            <option value="other">Not sure / several</option>
                        </select>
                    </Field>
                )}

                <Field label="Full name" error={errors.full_name}>
                    <input
                        type="text"
                        name="name"
                        autoComplete="name"
                        className={inputBase}
                        placeholder="Jane Smith"
                        value={data.full_name}
                        onChange={(e) => update({ full_name: e.target.value })}
                        data-testid="quickquote-name"
                    />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Phone" error={errors.phone}>
                        <input
                            type="tel"
                            name="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            className={inputBase}
                            placeholder="416 555 0123"
                            value={data.phone}
                            onChange={(e) => update({ phone: e.target.value })}
                            data-testid="quickquote-phone"
                        />
                    </Field>
                    <Field label="City" error={errors.city}>
                        <input
                            type="text"
                            name="city"
                            autoComplete="address-level2"
                            className={inputBase}
                            placeholder={copy.cityPlaceholder}
                            value={data.city}
                            onChange={(e) => update({ city: e.target.value })}
                            data-testid="quickquote-city"
                        />
                    </Field>
                </div>

                <Field label="Email" error={errors.email}>
                    <input
                        type="email"
                        name="email"
                        inputMode="email"
                        autoComplete="email"
                        className={inputBase}
                        placeholder="jane@example.com"
                        value={data.email}
                        onChange={(e) => update({ email: e.target.value })}
                        data-testid="quickquote-email"
                    />
                </Field>

                {isB2B && (
                    <Field label="Your role (optional)">
                        <select
                            name="role"
                            className={selectBase}
                            value={data.role}
                            onChange={(e) => update({ role: e.target.value })}
                            data-testid="quickquote-role"
                        >
                            <option value="">Select…</option>
                            {B2B_ROLES.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </Field>
                )}

                <div>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/70 mb-1.5">
                        Timeline
                    </span>
                    <div className="grid grid-cols-3 gap-2" role="group" aria-label="Timeline">
                        {QUICK_TIMELINES.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => update({ timeline: t.id })}
                                aria-pressed={data.timeline === t.id}
                                className={cn(
                                    "border-2 px-2 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors duration-150",
                                    data.timeline === t.id
                                        ? "border-brand-cyan bg-brand-cyan text-white"
                                        : "border-brand-dark/15 bg-white text-brand-dark hover:border-brand-cyan",
                                )}
                                data-testid={`quickquote-timeline-${t.id}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    {errors.timeline ? (
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-brand-coral">
                            <AlertCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                            {errors.timeline}
                        </p>
                    ) : null}
                </div>
            </div>

            {serverError ? (
                <p
                    className="mt-4 flex items-start gap-2 bg-brand-coral/10 border border-brand-coral/30 p-3 text-sm text-brand-coral"
                    data-testid="quickquote-error"
                >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2.5} />
                    {serverError}
                </p>
            ) : null}

            <BrandButton
                type="submit"
                variant="primary"
                size="lg"
                disabled={submitting}
                className="mt-5 w-full"
                data-testid="quickquote-submit"
            >
                {submitting ? (
                    <>
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                        Sending…
                    </>
                ) : (
                    copy.submit
                )}
            </BrandButton>

            <p className="mt-3 flex items-center gap-1.5 text-xs text-brand-gray">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
                No obligation. We never share your details.
            </p>
            <p className="mt-2 text-xs text-brand-dark/60">
                Prefer to talk?{" "}
                <a
                    href={phoneHref}
                    className="font-semibold text-brand-cyan hover:text-brand-coral transition-colors"
                    data-testid="quickquote-call"
                >
                    Call {displayPhone}
                </a>
            </p>
        </form>
    );
};

export default QuickQuoteForm;
